import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import { useState } from "react";
import { Loader2 } from "lucide-react";

import { PageHeader } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUserProfile } from "@/components/auth";
import { CheckoutReturnNotice, useCheckoutReturnNotice, type CheckoutNotice } from "@/components/billing/checkout-return-notice";
import { OrganizationBillingCard, type BillingOverview } from "@/components/billing/organization-billing-card";
import { OrganizationGeneralPanel } from "@/features/organization/organization-general-panel";
import {
  ORGANIZATION_TAB_LABELS,
  liveCheckoutSearch,
  parseOrganizationSearch,
  resolveOrganizationTab,
  visibleOrganizationTabs,
  type OrganizationTab,
} from "@/features/organization/organization-tabs";
import { MemberChangeHistoryCard } from "@/features/organization-members/member-change-history-card";
import { MembersPanel } from "@/features/organization-members/members-table";
import { useViewerCapabilities } from "@/lib/capabilities";
import { readCheckoutReturn, useBillingSession } from "@/lib/billing-session";

export const Route = createFileRoute("/organization")({
  validateSearch: parseOrganizationSearch,
  component: OrganizationSettingsPage,
});

function BillingPanel(props: {
  canManageBilling: boolean;
  hasOrganization: boolean;
  checkoutNotice: CheckoutNotice | null;
  billingSession: ReturnType<typeof useBillingSession>;
}) {
  const { billingSession } = props;
  const {
    data: billingOverview,
    isPending,
    error,
  } = useQuery(
    convexQuery(api.billing.queries.getOrganizationBillingOverview, props.hasOrganization ? {} : "skip")
  );

  if (isPending) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading billing…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground" role="alert">
          Billing details couldn't load. Refresh the page to try again.
        </CardContent>
      </Card>
    );
  }
  const overview = billingOverview as BillingOverview;
  return (
    <OrganizationBillingCard
      billingOverview={overview}
      canManageBilling={props.canManageBilling}
      pendingBillingAction={billingSession.pendingAction}
      notice={<CheckoutReturnNotice notice={props.checkoutNotice} accessState={overview.summary.state} />}
      onStartSubscription={() => void billingSession.open("auto")}
      onManageBilling={() => void billingSession.open("manage")}
    />
  );
}

export function OrganizationSettingsPage() {
  const { userProfile: viewer } = useUserProfile();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const capabilities = useViewerCapabilities();
  const canManageOrg = capabilities?.canManageMembers ?? false;
  const canManageBilling = capabilities?.canManageBilling ?? false;
  const tabs = visibleOrganizationTabs(canManageOrg);
  const [isCheckoutReturn] = useState(() => readCheckoutReturn(window.location.search) !== null);
  const activeTab = resolveOrganizationTab(search, canManageOrg, isCheckoutReturn);
  // Page-level so a Checkout confirmation or billing redirect survives switching tabs.
  const checkoutNotice = useCheckoutReturnNotice(Boolean(viewer?.organizationId) && canManageBilling);
  const billingSession = useBillingSession();

  const selectTab = (tab: string) => {
    void navigate({ search: { ...liveCheckoutSearch(), tab: tab as OrganizationTab }, replace: true });
  };

  if (viewer === null) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Tabs value={activeTab} onValueChange={selectTab} className="gap-4">
      <PageHeader
        title="Organization"
        actions={
          <TabsList>
            {tabs.map((tab) => (
              <TabsTrigger key={tab} value={tab} className="px-3">
                {ORGANIZATION_TAB_LABELS[tab]}
              </TabsTrigger>
            ))}
          </TabsList>
        }
      />

      {canManageOrg ? (
        <TabsContent value="members">
          <MembersPanel viewerUserId={viewer?.userId} viewerRole={viewer?.orgRole} />
        </TabsContent>
      ) : null}
      <TabsContent value="billing">
        <BillingPanel
          canManageBilling={canManageBilling}
          hasOrganization={Boolean(viewer?.organizationId)}
          checkoutNotice={checkoutNotice}
          billingSession={billingSession}
        />
      </TabsContent>
      <TabsContent value="general">
        <OrganizationGeneralPanel
          organizationName={viewer?.organizationName}
          viewerRole={viewer?.orgRole}
          canManageOrg={canManageOrg}
        />
      </TabsContent>
      {canManageOrg ? (
        <TabsContent value="activity">
          <MemberChangeHistoryCard />
        </TabsContent>
      ) : null}
    </Tabs>
  );
}
